import { useEffect } from 'react';
import { Banner } from './ui';
import type { Block } from '../content/blocks';

/** Set the document title and description for a route.
 *
 *  This is a single-page app, so without it every article and legal page
 *  shares the shell's title — wrong in a browser tab, wrong in a bookmark,
 *  and wrong for anything that reads the page after JavaScript has run. */
export function useDocumentMeta(title: string, description: string) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    const previousDescription = tag.getAttribute('content') ?? '';
    tag.setAttribute('content', description);
    return () => {
      document.title = previous;
      tag?.setAttribute('content', previousDescription);
    };
  }, [title, description]);
}

/** One content block. Shared by articles and legal documents so typography
 *  is fixed in one place rather than two. */
export function BlockView({ block, id }: { block: Block; id?: string }) {
  switch (block.kind) {
    case 'h':
      return <h2 className="prose__h" id={id}>{block.text}</h2>;
    case 'p':
      return <p className="prose__p">{block.text}</p>;
    case 'ul':
      return (
        <ul className="prose__list">
          {block.items.map((item, index) => <li key={index}>{item}</li>)}
        </ul>
      );
    case 'ol':
      return (
        <ol className="prose__list prose__list--ordered">
          {block.items.map((item, index) => <li key={index}>{item}</li>)}
        </ol>
      );
    case 'quote':
      return <blockquote className="prose__quote">{block.text}</blockquote>;
    case 'note':
      return (
        <div style={{ margin: '20px 0' }}>
          <Banner tone={block.tone} title={block.title}>{block.text}</Banner>
        </div>
      );
    case 'table':
      return (
        <div className="table-wrap" style={{ margin: '20px 0' }}>
          <table className="table">
            <thead>
              <tr>{block.head.map((cell) => <th key={cell}>{cell}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, index) => (
                <tr key={index}>{row.map((cell, i) => <td key={i}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}
